// @ts-nocheck
/**
 * pdp-gallery.js
 * ─────────────────────────────────────────────────────────────
 * New PDP gallery: vertical INFINITE thumbnail strip (desktop)
 * + horizontal scroll-snap with custom scrollbar (mobile).
 *
 * Infinite scroll technique: clone-based triple-set
 *   [prepend-clones] [real-thumbs] [append-clones]
 * When the viewport drifts into a clone zone we silently
 * teleport back to the equivalent position in the real zone.
 * Content is identical so the seam is invisible.
 *
 * Depends on globals: subscribe, PUB_SUB_EVENTS  (pubsub.js + constants.js)
 * ─────────────────────────────────────────────────────────────
 */

/* ── media-gallery stub ──────────────────────────────────────
   Keeps product-info.js working:
   • querySelector("media-gallery ul")  → .pdp-compat-list
   • media-gallery.setActiveMedia()     → forwarded to pdp-gallery
   ───────────────────────────────────────────────────────────── */
if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      setActiveMedia(mediaId, prepend) {
        // When prepend=true, product-info.js is responding to a non-color
        // variant change (e.g. size). pdp-gallery handles color changes via
        // its own pubsub subscription, so skip forwarding here to prevent
        // the thumbnail strip from jumping when only size changes.
        if (prepend) return;
        this.querySelector("pdp-gallery")?.setActiveMedia(mediaId);
      }
    },
  );
}

/* ── pdp-gallery custom element ─────────────────────────────── */
if (!customElements.get("pdp-gallery")) {
  // Pinch-to-zoom sharpening (mobile): when the visual viewport is zoomed in,
  // the browser only magnifies the already-painted ~1100px slide — it never
  // re-fetches a sharper source from srcset. So we upgrade the in-view slide's
  // image on demand to this width (capped at the image's native resolution).
  // Tunable: higher = crisper at deep zoom, but a heavier on-zoom download.
  const ZOOM_MAX_WIDTH = 2048;
  // Ignore tiny zooms — the base image is still sharp enough below this.
  const ZOOM_MIN_SCALE = 1.15;

  class PdpGallery extends HTMLElement {
    connectedCallback() {
      this._viewport = this.querySelector(".pdp-g__thumbs-viewport");
      this._thumbsList = this._viewport?.querySelector(".pdp-g__thumbs-list");
      this._jumpLocked = false;
      this._topThumbIdx = 0;
      if (typeof ResizeObserver !== "undefined") {
        this._resizeObserver = new ResizeObserver(() =>
          this._onViewportResize(),
        );
      }
      this._mobileScrollEndTimer = null;
      this._arrowTimer = null;
      this._unsub = null;
      this._unsubVariant = null;
      this._pageLoaded = document.readyState === "complete";
      this._thumbVideoObserver = null;
      this._mobileVideoObserver = null;
      this._mainViewObserver = null;
      this._mainInView = false;
      this._onVVResize = null;
      this._zoomRaf = 0;

      this._setupArrows();
      this._setupMobile();
      this._setupZoom();
      this._setupVideos();
      this._scheduleMediaWarm();

      // Correct color from URL before first paint — prevents flash of the
      // server-rendered default variant when a ?color= param is present.
      this._applyUrlColorFilter();

      // Start the visible hero video immediately instead of waiting for window
      // 'load' (see _primeActiveHero). Runs after the color filter so it targets
      // the corrected active item; the deferred observers below still run and
      // are no-ops for this one video thanks to its idempotent load guards.
      this._primeActiveHero();

      // Clones need post-layout values
      requestAnimationFrame(() => {
        this._buildInfiniteClones();
        this._buildMobileClones();
        this._setupViewportScroll();
        // Reveal gallery after clones have settled into correct state.
        requestAnimationFrame(() => this.classList.add("pdp-g--ready"));
      });

      // Wait for pubsub globals (loaded via defer, may race)
      const init = () => {
        if (
          typeof subscribe === "undefined" ||
          typeof PUB_SUB_EVENTS === "undefined"
        ) {
          requestAnimationFrame(init);
          return;
        }
        this._unsub = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this._onColorChange.bind(this),
        );
        this._unsubVariant = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this._onVariantChange.bind(this),
        );

        // Sync initial wishlist state once Wishlist Hero is ready.
        const self = this;
        function syncInitial() {
          const sdk = window.WishListHero_SDK;
          if (!sdk || typeof sdk.GetWishListItems !== "function") return;
          const btns = self.querySelectorAll(".swym-wishlist-button");
          if (!btns.length) return;
          let list;
          try {
            list =
              typeof WishlistManager !== "undefined" &&
              WishlistManager.getEffectiveItems
                ? WishlistManager.getEffectiveItems()
                : null;
            if (list === null) list = sdk.GetWishListItems();
          } catch (_) {
            list = [];
          }
          if (!Array.isArray(list)) list = [];
          const wishlisted = new Set(
            list.map(function (item) {
              return String(item.ProductVariantId);
            }),
          );
          btns.forEach(function (btn) {
            const vid = btn.getAttribute("data-variant-id");
            btn.classList.toggle(
              "swym-added",
              !!vid && wishlisted.has(vid),
            );
          });
        }
        if (window.WishListHero_SDK) syncInitial();
        document.addEventListener(
          "wishlist-hero-wishlist-sdk-ready",
          syncInitial,
        );
      };
      init();
    }

    disconnectedCallback() {
      this._unsub?.();
      this._unsubVariant?.();
      this._resizeObserver?.disconnect();
      this._thumbVideoObserver?.disconnect();
      this._mobileVideoObserver?.disconnect();
      this._mainViewObserver?.disconnect();
      if (this._onVVResize) {
        window.visualViewport?.removeEventListener("resize", this._onVVResize);
      }
      if (this._zoomRaf) cancelAnimationFrame(this._zoomRaf);
    }

    /* ═══════════════════════════════════════════════════════════
       Gallery videos – defer playback until the page is fully
       loaded, then play only the ones actually visible (thumbs,
       mobile slides, active main item). Markup ships them with
       NO src (URL parked in data-lv-src) + preload="none" + poster,
       so zero video bytes are fetched until we inject the src and
       opt in here — see _ensureVideoSrc.
    ═══════════════════════════════════════════════════════════ */
    _setupVideos() {
      if (this._pageLoaded) {
        this._playThumbVideos();
        this._observeMobileVideos();
        this._observeMainViewer();
        return;
      }
      window.addEventListener(
        "load",
        () => {
          this._pageLoaded = true;
          this._playThumbVideos();
          this._observeMobileVideos();
          this._observeMainViewer();
        },
        { once: true },
      );
    }

    /**
     * (Re)attach visibility-driven playback to every thumb video (real +
     * clones). Idempotent: safe to call again after clones are rebuilt.
     */
    _playThumbVideos() {
      const videos = this.querySelectorAll(".pdp-g__thumb video");
      if (!videos.length) return;

      // No IntersectionObserver → just start them all once.
      if (typeof IntersectionObserver === "undefined") {
        videos.forEach((v) => this._playVideoWhenReady(v));
        return;
      }

      if (!this._thumbVideoObserver) {
        this._thumbVideoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const v = entry.target;
              if (entry.isIntersecting) {
                v._lvWant = true;
                this._playVideoWhenReady(v);
              } else {
                v._lvWant = false;
                v.pause();
              }
            });
          },
          {
            root: this._viewport || null,
            rootMargin: "150px 0px",
            threshold: 0.01,
          },
        );
      } else {
        // Drop stale clone references before re-observing the current set.
        this._thumbVideoObserver.disconnect();
      }

      videos.forEach((v) => this._thumbVideoObserver.observe(v));
    }

    /**
     * (Re)attach visibility-driven playback to every mobile-slide video
     * (real + clones). Hidden colors are display:none (.pdp-hidden) so they
     * never intersect and cost zero bytes. threshold 0.5: the next slide
     * peeking a few px during a swipe must not start streaming.
     * Idempotent: safe to call again after clones are rebuilt.
     */
    _observeMobileVideos() {
      const videos = this.querySelectorAll(".pdp-g__mobile-item video");
      if (!videos.length) return;

      // No IntersectionObserver → play just the active slide's video.
      if (typeof IntersectionObserver === "undefined") {
        this._playVideoWhenReady(
          this.querySelector(".pdp-g__mobile-item.is-active video"),
        );
        return;
      }

      if (!this._mobileVideoObserver) {
        this._mobileVideoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const v = entry.target;
              if (entry.isIntersecting) {
                v._lvWant = true;
                this._playVideoWhenReady(v);
              } else {
                v._lvWant = false;
                v.pause();
              }
            });
          },
          { threshold: 0.5 },
        );
      } else {
        // Drop stale clone references before re-observing the current set.
        this._mobileVideoObserver.disconnect();
      }

      videos.forEach((v) => this._mobileVideoObserver.observe(v));
    }

    /**
     * Watch the desktop main viewer as a whole (the stacked .pdp-g__main-item
     * crossfade layers all "intersect" at opacity:0, so observing them
     * individually would play every video of the active color at once).
     * Plays the active item's video when the viewer scrolls into view,
     * pauses everything when it leaves.
     */
    _observeMainViewer() {
      const main = this.querySelector(".pdp-g__main");
      if (!main || !main.querySelector("video")) return;

      if (typeof IntersectionObserver === "undefined") {
        this._mainInView = true;
        this._playActiveMainVideo();
        return;
      }

      if (this._mainViewObserver) this._mainViewObserver.disconnect();
      this._mainViewObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this._mainInView = entry.isIntersecting;
            if (entry.isIntersecting) {
              this._playActiveMainVideo();
            } else {
              main.querySelectorAll("video").forEach((v) => v.pause());
            }
          });
        },
        { threshold: 0.1 },
      );
      this._mainViewObserver.observe(main);
    }

    _playActiveMainVideo() {
      const v = this.querySelector(".pdp-g__main-item.is-active video");
      if (v) this._playMainVideo(v);
    }

    /**
     * The one hero video the user sees on load must NOT wait for window 'load'
     * the way _setupVideos does — that event blocks on every other image, font
     * and script on the PDP, so the hero sits frozen on its poster for seconds
     * before it even begins buffering. Start ONLY the active, on-screen hero
     * right away (this runs at connect, ~DOMContentLoaded): inject its parked
     * src and play as soon as the first frame decodes. Its bytes are already in
     * flight from the <link rel=preload as=video> in the markup, so this
     * usually plays almost immediately. Everything else — thumbs, other colors,
     * the hidden viewport, the full-media warm-up — stays gated on window
     * 'load', so the critical path and byte-saving guarantees are unchanged.
     * Idempotent: it sets the same _lvLoadStarted / src guards the window-load
     * path checks, so the observers that fire later never double-fetch. No-op
     * when the hero is an image (no <video> in the active item).
     */
    _primeActiveHero() {
      const desktop =
        !window.matchMedia || window.matchMedia("(min-width: 990px)").matches;
      const v = desktop
        ? this.querySelector(".pdp-g__main-item.is-active video")
        : this.querySelector(".pdp-g__mobile-item.is-active video");
      // Only the viewport actually on screen — the other viewer is display:none
      // and playing it would silently stream its file behind the scenes.
      if (!v || !v.getClientRects().length) return;
      this._ensureVideoSrc(v);
      if (v.preload !== "auto") v.preload = "auto";
      const play = () => v.play().catch(() => {});
      if (v.readyState >= 2) {
        play();
      } else if (!v._lvReadyBound) {
        v._lvReadyBound = true;
        const onReady = () => {
          v.removeEventListener("loadeddata", onReady);
          v._lvReadyBound = false;
          if (v._lvWant !== false) play();
        };
        v.addEventListener("loadeddata", onReady);
      }
      if (!v._lvLoadStarted) {
        v._lvLoadStarted = true;
        v.load();
      }
    }

    /**
     * Inject the deferred source URL the first time a video is actually
     * activated. Markup ships these videos with NO src and the file URL parked
     * in data-lv-src, so the browser fetches zero video bytes until a visible
     * thumb/slide/main item opts in here. preload="none" alone is not enough:
     * Safari range-requests the moov atom of any <source> even under
     * display:none, which is why hidden-color videos were downloading.
     * No-op once a src is present (idempotent across re-observes/replays).
     */
    _ensureVideoSrc(v) {
      if (!v || v.getAttribute("src")) return;
      const src = v.dataset.lvSrc;
      if (src) v.src = src;
    }

    /**
     * Begin playback only once the first frame is decoded. Activating a
     * preload="none" video (set preload="auto" + play() immediately) makes the
     * browser drop the poster before any frame exists — a black flash — then
     * stall while it buffers (the "flash → pause → play" seen on first
     * activation). Waiting for loadeddata lets the poster→first-frame swap
     * happen seamlessly, then plays from the already-painted frame.
     * _lvWant (set by the observers) lets us skip the deferred play if the
     * video scrolled out of view before its first frame arrived.
     */
    _playVideoWhenReady(v) {
      if (!v) return;
      this._ensureVideoSrc(v);
      if (v.preload !== "auto") v.preload = "auto";
      if (v.readyState >= 2) {
        v.play().catch(() => {});
        return;
      }
      if (v._lvReadyBound) return; // don't stack listeners across re-observes
      v._lvReadyBound = true;
      const onReady = () => {
        v.removeEventListener("loadeddata", onReady);
        v._lvReadyBound = false;
        if (v._lvWant !== false) v.play().catch(() => {});
      };
      v.addEventListener("loadeddata", onReady);
      // Flipping preload alone doesn't reliably start the fetch for a video
      // parsed with preload="none" (its <source> children are already resolved
      // and suspended). load() kicks off the download so loadeddata fires.
      v.load();
    }

    /** Play a main-viewer video — only once it can actually be seen. */
    _playMainVideo(v) {
      if (!this._pageLoaded || !this._mainInView) return;
      // The desktop viewer is display:none on mobile; playing it there would
      // silently stream the full file behind the scenes.
      if (!v.getClientRects().length) return;
      this._ensureVideoSrc(v);
      if (v.preload !== "auto") v.preload = "auto";
      const start = () => {
        // Re-verify still active + in view at play time: the user may have
        // swapped color/slide while the first frame was loading.
        if (!this._mainInView) return;
        const item = v.closest(".pdp-g__main-item");
        if (item && !item.classList.contains("is-active")) return;
        if (!v.getClientRects().length) return;
        v.play().catch(() => {});
      };
      if (v.readyState >= 2) {
        start();
        return;
      }
      if (!v._lvReadyBound) {
        v._lvReadyBound = true;
        const onReady = () => {
          v.removeEventListener("loadeddata", onReady);
          v._lvReadyBound = false;
          start();
        };
        v.addEventListener("loadeddata", onReady);
      }
      // No <source> child to auto-resolve under the deferred-src markup, so kick
      // the fetch off explicitly. load() restarts buffering, so trigger it only
      // on the first activation; preload="auto" keeps the file warm afterwards.
      if (!v._lvLoadStarted) {
        v._lvLoadStarted = true;
        v.load();
      }
    }

    /* ═══════════════════════════════════════════════════════════
       Background media warm-up (main product only — this component
       only renders on the PDP). The gallery defers everything by
       default: images are loading="lazy" (hidden-color ones never
       fetch) and videos ship with no src. That keeps the critical
       path lean, but switching color or scrolling then pays a fresh
       download. Here we proactively pull EVERY color's media into the
       HTTP cache — but only after window 'load' AND during idle time,
       so it never competes with the LCP image or first paint. Result:
       once the page is settled, color swaps and scroll are instant.

       Scope is the active viewport's renditions only (desktop: 480p
       thumbs + 1080p main; mobile: 720p slides). The other viewport
       serves different URLs the user can't see on this device, so
       fetching them would be pure waste — the same per-viewport logic
       the top-of-snippet LCP <link rel=preload> already follows.
    ═══════════════════════════════════════════════════════════ */
    _scheduleMediaWarm() {
      const run = () => {
        const idle =
          window.requestIdleCallback ||
          ((cb) => window.setTimeout(cb, 200));
        idle(() => this._warmAllMedia(), { timeout: 3000 });
      };
      if (this._pageLoaded) {
        run();
        return;
      }
      window.addEventListener("load", run, { once: true });
    }

    _warmAllMedia() {
      if (this._mediaWarmed) return;
      this._mediaWarmed = true;

      const desktop =
        !window.matchMedia ||
        window.matchMedia("(min-width: 990px)").matches;

      let imgs;
      let videos;
      if (desktop) {
        // ` > img` skips the nested badge image; only the media itself.
        imgs = [
          ...this.querySelectorAll(".pdp-g__thumb:not(.pdp-g__clone) > img"),
          ...this.querySelectorAll(".pdp-g__main-item > img"),
        ];
        videos = [
          ...this.querySelectorAll(".pdp-g__thumb:not(.pdp-g__clone) video"),
          ...this.querySelectorAll(".pdp-g__main-item video"),
        ];
      } else {
        imgs = [
          ...this.querySelectorAll(
            ".pdp-g__mobile-item:not(.pdp-g__mobile-clone) > img",
          ),
        ];
        videos = [
          ...this.querySelectorAll(
            ".pdp-g__mobile-item:not(.pdp-g__mobile-clone) video",
          ),
        ];
      }

      imgs.forEach((img) => this._warmImg(img));
      videos.forEach((v) => this._warmVideo(v));
    }

    /**
     * Warm one gallery image without disturbing the live element. A detached
     * Image mirrors the real one's srcset/sizes/src, so the browser selects
     * and fetches the SAME candidate URL — populating the HTTP cache — while
     * the on-page <img loading="lazy"> keeps its carefully tuned lazy/eager
     * state untouched and simply hits cache when its color/scroll reveals it.
     */
    _warmImg(img) {
      if (!img || img._lvWarmed) return;
      img._lvWarmed = true;
      const warm = new Image();
      // sizes/srcset first so candidate selection matches the real <img>.
      if (img.sizes) warm.sizes = img.sizes;
      if (img.srcset) warm.srcset = img.srcset;
      if (img.src) warm.src = img.src;
      // Hold a reference: an unreferenced detached Image can be GC'd before
      // the fetch finishes, aborting the warm-up. Release once cached.
      (this._warmRefs || (this._warmRefs = [])).push(warm);
      const done = () => {
        const i = this._warmRefs.indexOf(warm);
        if (i !== -1) this._warmRefs.splice(i, 1);
      };
      warm.addEventListener("load", done, { once: true });
      warm.addEventListener("error", done, { once: true });
    }

    /**
     * Warm one deferred gallery video: inject the parked data-lv-src and let
     * preload="auto" + load() pull the file. Reuses _ensureVideoSrc and the
     * _lvLoadStarted guard the playback path checks, so this is idempotent and
     * a later activation just play()s the already-buffered file (no re-fetch).
     * No play() here — warming is download-only.
     */
    _warmVideo(v) {
      if (!v || v.getAttribute("src")) return;
      this._ensureVideoSrc(v);
      if (v.preload !== "auto") v.preload = "auto";
      if (!v._lvLoadStarted) {
        v._lvLoadStarted = true;
        v.load();
      }
    }

    // Update data-variant-id on all gallery wishlist buttons and sync the
    // swym-added state using the Wishlist Hero cached items.
    _syncWishlistBtns(variantId) {
      if (!variantId) return;
      var vid = String(variantId);
      var btns = this.querySelectorAll(".swym-wishlist-button");
      btns.forEach(function (btn) {
        btn.setAttribute("data-variant-id", vid);
      });
      var sdk = window.WishListHero_SDK;
      if (!sdk || typeof sdk.GetWishListItems !== "function") return;
      var list;
      try {
        list =
          typeof WishlistManager !== "undefined" &&
          WishlistManager.getEffectiveItems
            ? WishlistManager.getEffectiveItems()
            : null;
        if (list === null) list = sdk.GetWishListItems();
      } catch (_) {
        list = [];
      }
      if (!Array.isArray(list)) list = [];
      var found = list.some(function (item) {
        return String(item.ProductVariantId) === vid;
      });
      btns.forEach(function (btn) {
        btn.classList.toggle("swym-added", found);
      });
    }

    _onVariantChange(evt) {
      const variant = evt?.data?.variant;
      if (!variant?.id) return;
      this._syncWishlistBtns(variant.id);
    }

    /* ═══════════════════════════════════════════════════════════
       Synchronous URL-color correction (called before first paint)
       Reads the ?color= URL param and, if it differs from the
       server-rendered variant, filters the gallery immediately so
       the browser never paints the wrong color.
    ═══════════════════════════════════════════════════════════ */
    _applyUrlColorFilter() {
      try {
        const colorKey = (this.dataset.colorOptionName || "color").replace(
          /\s+/g,
          "-",
        );
        const params = new URLSearchParams(window.location.search);
        const urlColorRaw = params.get(colorKey);
        if (!urlColorRaw) return;
        const urlColor = urlColorRaw.toLowerCase().replace(/-/g, " ").trim();
        const serverColor = (this.dataset.selectedColor || "")
          .toLowerCase()
          .trim();
        if (urlColor === serverColor) return; // server already rendered the right color
        const isOos = this._isColorOos(urlColor);
        this._filterByColor(urlColor, isOos);
      } catch (_) {}
    }

    /* ═══════════════════════════════════════════════════════════
       Arrow buttons – scroll by one item, loop handled by jump
    ═══════════════════════════════════════════════════════════ */
    _setupArrows() {
      this.querySelector(".pdp-g__arrow--up")?.addEventListener("click", () =>
        this._onArrow(-1),
      );
      this.querySelector(".pdp-g__arrow--down")?.addEventListener("click", () =>
        this._onArrow(1),
      );

      // Real thumb click listeners (clones get their own listeners in makeClone)
      this.querySelectorAll(".pdp-g__thumb:not(.pdp-g__clone)").forEach(
        (th) => {
          th.addEventListener("click", () =>
            this.setActiveMedia(th.dataset.mediaId),
          );
        },
      );
    }

    _onArrow(dir) {
      if (!this._thumbsList || !this._viewport) return;

      // Find all visible real thumbs in DOM order
      const allVis = [
        ...this._thumbsList.querySelectorAll(
          ".pdp-g__thumb:not(.pdp-g__clone):not(.pdp-hidden)",
        ),
      ];
      if (!allVis.length) return;

      // Find the currently active one, default to first
      const activeIdx = allVis.findIndex((th) =>
        th.classList.contains("is-active"),
      );
      const currentIdx = activeIdx >= 0 ? activeIdx : 0;

      // Step one forward/back, wrapping around
      const nextIdx = (currentIdx + dir + allVis.length) % allVis.length;

      // Scroll the viewport by exactly one thumbnail step on every click
      const step =
        allVis.length >= 2
          ? allVis[1].offsetTop - allVis[0].offsetTop
          : allVis[0].offsetHeight + this._getGap();
      this._viewport.scrollBy({ top: dir * step, behavior: "smooth" });

      // Update active state + main image without triggering a second scroll
      this.setActiveMedia(
        allVis[nextIdx].dataset.mediaId,
        true /* skipScroll */,
      );
    }

    /** Gap between two adjacent visible items (px) */
    _getGap() {
      if (!this._thumbsList) return 20;
      const vis = [
        ...this._thumbsList.querySelectorAll(
          ".pdp-g__thumb:not(.pdp-g__clone):not(.pdp-hidden)",
        ),
      ];
      if (vis.length < 2) return 20;
      return vis[1].offsetTop - vis[0].offsetTop - vis[0].offsetHeight;
    }

    /** Find whichever thumb is closest to the viewport centre and activate it */
    _activateCenteredThumb() {
      if (!this._viewport || !this._thumbsList) return;
      const vpCentre =
        this._viewport.scrollTop + this._viewport.clientHeight / 2;
      const allVis = [
        ...this._thumbsList.querySelectorAll(".pdp-g__thumb:not(.pdp-hidden)"),
      ];
      if (!allVis.length) return;

      let closest = allVis[0];
      let minDist = Infinity;
      allVis.forEach((th) => {
        const dist = Math.abs(th.offsetTop + th.offsetHeight / 2 - vpCentre);
        if (dist < minDist) {
          minDist = dist;
          closest = th;
        }
      });

      this.setActiveMedia(closest.dataset.mediaId, true /* skipScroll */);
    }

    /* ═══════════════════════════════════════════════════════════
       Infinite-scroll clone machinery
    ═══════════════════════════════════════════════════════════ */

    /** Track which real-thumb index is at (or just above) the viewport top */
    _trackTopThumb() {
      if (!this._viewport || !this._thumbsList) return;
      const st = this._viewport.scrollTop;
      const realThumbs = [
        ...this._thumbsList.querySelectorAll(
          ".pdp-g__thumb:not(.pdp-g__clone):not(.pdp-hidden)",
        ),
      ];
      let idx = 0;
      for (let i = 0; i < realThumbs.length; i++) {
        if (realThumbs[i].offsetTop <= st) idx = i;
        else break;
      }
      this._topThumbIdx = idx;
    }

    /**
     * Called by the ResizeObserver whenever the thumbnail viewport resizes.
     * A window resize changes the viewport height (the strip stretches to the
     * main image), which would otherwise let the strip drift down by one image
     * per resize. Re-pin scrollTop so the FIRST real thumb always stays at top.
     */
    _onViewportResize() {
      this._jumpToRealZone();
    }

    /**
     * Build the triple-set:  [clones] [real] [clones]
     * Called once on init and again after every color-filter change.
     */
    _buildInfiniteClones() {
      if (!this._thumbsList) return;

      // Remove old clones
      this._thumbsList
        .querySelectorAll(".pdp-g__clone")
        .forEach((c) => c.remove());

      // Normalise: physically sort visible real thumbs in visual (CSS-order) order
      // and clear the CSS order property so offsetTop maths stays simple.
      this._normalizeOrder();

      // Only clone visible thumbs – hidden items should not appear in the loop
      const realThumbs = [
        ...this._thumbsList.querySelectorAll(
          ".pdp-g__thumb:not(.pdp-g__clone):not(.pdp-hidden)",
        ),
      ];
      if (realThumbs.length < 2) return; // nothing to loop

      const makeClone = (th) => {
        const cl = th.cloneNode(true);
        cl.classList.add("pdp-g__clone");
        cl.removeAttribute("id");
        // A playing original reflects preload="auto" into its attribute, which
        // cloneNode copies — the clone would fetch the file the moment it's
        // attached. Reset so the observer decides when (and if) it loads.
        const cv = cl.querySelector("video");
        if (cv) {
          cv.removeAttribute("autoplay");
          // Drop any src the original injected once it was activated, so the
          // clone re-defers to data-lv-src (preserved by cloneNode) and only
          // fetches when the observer activates it.
          cv.removeAttribute("src");
          cv.setAttribute("preload", "none");
        }
        cl.addEventListener("click", () =>
          this.setActiveMedia(cl.dataset.mediaId),
        );
        return cl;
      };

      // Prepend set
      const before = document.createDocumentFragment();
      realThumbs.forEach((th) => before.appendChild(makeClone(th)));
      this._thumbsList.prepend(before);

      // Append set
      const after = document.createDocumentFragment();
      realThumbs.forEach((th) => after.appendChild(makeClone(th)));
      this._thumbsList.appendChild(after);

      // Start scroll at top of real zone (after browser reflows clones)
      requestAnimationFrame(() => this._jumpToRealZone());

      // Observe the viewport (not a thumb): its height changes on window
      // resize, so this fires _onViewportResize() which re-pins the strip to
      // the first thumb. Watching a thumb wouldn't work — thumbs are a fixed
      // size now and never resize.
      if (this._resizeObserver && this._viewport) {
        this._resizeObserver.disconnect();
        this._resizeObserver.observe(this._viewport);
      }

      // Cloned thumb videos start paused — hook playback up to the new set
      // (only once the page has finished loading).
      if (this._pageLoaded) this._playThumbVideos();
    }

    /**
     * Physically reorder DOM so visible thumbs come first (sorted by their
     * current offsetTop, which already reflects CSS flex `order`), then
     * hidden thumbs. After this the CSS `order` property is cleared.
     */
    _normalizeOrder() {
      if (!this._thumbsList) return;
      const all = [
        ...this._thumbsList.querySelectorAll(
          ".pdp-g__thumb:not(.pdp-g__clone)",
        ),
      ];
      const visible = all.filter((t) => !t.classList.contains("pdp-hidden"));
      const hidden = all.filter((t) => t.classList.contains("pdp-hidden"));

      visible.sort((a, b) => a.offsetTop - b.offsetTop);

      [...visible, ...hidden].forEach((th) => {
        th.style.order = "";
        this._thumbsList.appendChild(th);
      });
    }

    /** Scroll viewport so the first real (non-clone) visible thumb is at top */
    _jumpToRealZone() {
      if (!this._viewport || !this._thumbsList) return;
      const firstReal = this._thumbsList.querySelector(
        ".pdp-g__thumb:not(.pdp-g__clone):not(.pdp-hidden)",
      );
      if (firstReal) this._viewport.scrollTop = firstReal.offsetTop;
    }

    /* ═══════════════════════════════════════════════════════════
       Viewport scroll handler – detect drift into clone zones
    ═══════════════════════════════════════════════════════════ */
    _setupViewportScroll() {
      if (!this._viewport) return;
      this._viewport.addEventListener(
        "scroll",
        () => {
          this._trackTopThumb();
          if (this._jumpLocked) return;
          this._checkInfiniteJump();
        },
        { passive: true },
      );
    }

    _checkInfiniteJump() {
      if (!this._viewport || !this._thumbsList) return;

      // Visible real thumbs in DOM order (= visual order after _normalizeOrder)
      const realThumbs = [
        ...this._thumbsList.querySelectorAll(
          ".pdp-g__thumb:not(.pdp-g__clone):not(.pdp-hidden)",
        ),
      ];
      if (!realThumbs.length) return;

      const firstReal = realThumbs[0];
      const lastReal = realThumbs[realThumbs.length - 1];

      // Find the first visible append clone (comes after last real in DOM)
      let firstAppend = null;
      let pastLastReal = false;
      for (const child of this._thumbsList.children) {
        if (child === lastReal) {
          pastLastReal = true;
          continue;
        }
        if (
          pastLastReal &&
          child.classList.contains("pdp-g__clone") &&
          !child.classList.contains("pdp-hidden")
        ) {
          firstAppend = child;
          break;
        }
      }
      if (!firstAppend) return;

      const realZoneSize = firstAppend.offsetTop - firstReal.offsetTop;
      const st = this._viewport.scrollTop;

      this._jumpLocked = true;
      if (st < firstReal.offsetTop) {
        // Drifted into prepend zone → jump forward
        this._viewport.scrollTop = st + realZoneSize;
      } else if (st >= firstAppend.offsetTop) {
        // Drifted into append zone → jump back
        this._viewport.scrollTop = st - realZoneSize;
      }
      this._jumpLocked = false;
    }

    /* ═══════════════════════════════════════════════════════════
       Public API: setActiveMedia
       skipScroll = true when called after arrow/scroll (already in position)
    ═══════════════════════════════════════════════════════════ */
    setActiveMedia(mediaId, skipScroll = false) {
      // Activate ALL copies of this thumb (real + clones)
      this.querySelectorAll(".pdp-g__thumb").forEach((t) => {
        t.classList.toggle("is-active", t.dataset.mediaId === mediaId);
      });

      if (!skipScroll) this._scrollThumbToActive(mediaId);

      // Update main view — skip if the target item is hidden (e.g. belongs to a
      // different color that product-info.js tried to activate via async fetch)
      const targetMain = this.querySelector(
        `.pdp-g__main-item[data-media-id="${mediaId}"]`,
      );
      if (targetMain && targetMain.classList.contains("pdp-hidden")) return;

      // A decode-gated color crossfade is in flight (see _filterByColor):
      // same media → let the gate finish the switch; different media (explicit
      // user pick) → the user wins, cancel the gate and activate immediately.
      if (this._mainGate) {
        if (this._mainGate.mediaId === mediaId) return;
        this._mainColorToken = (this._mainColorToken || 0) + 1;
        this._mainGate = null;
      }

      this.querySelectorAll(".pdp-g__main-item").forEach((m) => {
        const isActive = m.dataset.mediaId === mediaId;
        m.classList.toggle("is-active", isActive);
        const video = m.querySelector("video");
        if (video) {
          if (isActive) this._playMainVideo(video);
          else {
            video.pause();
            video.currentTime = 0;
          }
        }
      });
    }

    /**
     * Scroll the thumb copy that is nearest to the current viewport centre
     * into view – avoids a big jump when called programmatically.
     */
    _scrollThumbToActive(mediaId) {
      if (!this._viewport || !this._thumbsList) return;
      const copies = [
        ...this._thumbsList.querySelectorAll(
          `.pdp-g__thumb[data-media-id="${mediaId}"]:not(.pdp-hidden)`,
        ),
      ];
      if (!copies.length) return;

      const vpCentre =
        this._viewport.scrollTop + this._viewport.clientHeight / 2;
      let closest = copies[0];
      let minDist = Infinity;
      copies.forEach((th) => {
        const dist = Math.abs(th.offsetTop + th.offsetHeight / 2 - vpCentre);
        if (dist < minDist) {
          minDist = dist;
          closest = th;
        }
      });

      const top =
        closest.offsetTop -
        this._viewport.clientHeight / 2 +
        closest.offsetHeight / 2;
      const clampedTop = Math.max(0, top);

      // Skip scroll if the thumb is already fully visible inside the viewport
      const vpTop = this._viewport.scrollTop;
      const vpBottom = vpTop + this._viewport.clientHeight;
      const thumbTop = closest.offsetTop;
      const thumbBottom = thumbTop + closest.offsetHeight;
      if (thumbTop >= vpTop && thumbBottom <= vpBottom) return;

      this._viewport.scrollTo({ top: clampedTop, behavior: "smooth" });
    }

    /* ═══════════════════════════════════════════════════════════
       Mobile – infinite horizontal scroll + custom scrollbar
    ═══════════════════════════════════════════════════════════ */
    _setupMobile() {
      const list = this.querySelector(".pdp-g__mobile-list");
      const thumb = this.querySelector(".pdp-g__scrollbar-thumb");
      if (!list || !thumb) return;

      this._mobileList = list;
      this._mobileThumb = thumb;

      list.addEventListener("scroll", () => this._syncMobileScrollbar(), {
        passive: true,
      });

      // Back/forward navigation (incl. bfcache restore) can bring the strip
      // back at an arbitrary scroll offset — re-centre into the real zone and
      // re-sync the custom scrollbar so the position indicator is correct.
      window.addEventListener("pageshow", () => {
        requestAnimationFrame(() => {
          this._checkMobileInfiniteJump();
          this._syncMobileScrollbar();
        });
      });

      // Jump after scroll momentum fully settles — avoids fighting iOS
      // inertia and removes the overflow-toggle flash.
      if ("onscrollend" in list) {
        list.addEventListener(
          "scrollend",
          () => this._checkMobileInfiniteJump(),
          { passive: true },
        );
      } else {
        // Fallback: debounce scroll events for older Safari
        list.addEventListener(
          "scroll",
          () => {
            clearTimeout(this._mobileScrollEndTimer);
            this._mobileScrollEndTimer = setTimeout(
              () => this._checkMobileInfiniteJump(),
              80,
            );
          },
          { passive: true },
        );
      }
    }

    /* Build runway for mobile: [clones×2][real][clones×2]
       Two sets per side: the teleport back to the real zone only runs once
       scrolling settles (scrollend / debounce), so a burst of fast flings can
       travel more than one zone before we get a chance to jump. One set was
       escapable — the strip hit the native end and "continuous scroll" died. */
    _buildMobileClones() {
      const list = this._mobileList;
      if (!list) return;

      // Remove stale clones
      list.querySelectorAll(".pdp-g__mobile-clone").forEach((c) => c.remove());

      // Normalise: physically sort visible real items in visual (CSS-order)
      // order and clear the CSS order property. The server sets order:1/2 on
      // the OOS-fallback items; cloneNode copies that inline style, so a
      // leftover order would regroup every clone next to its original and
      // break the [clones][real][clones] runway (and the infinite-jump maths).
      this._normalizeMobileOrder();

      const real = [
        ...list.querySelectorAll(
          ".pdp-g__mobile-item:not(.pdp-g__mobile-clone):not(.pdp-hidden)",
        ),
      ];
      if (real.length < 2) return;

      const mkClone = (item) => {
        const c = item.cloneNode(true);
        c.classList.add("pdp-g__mobile-clone");
        c.classList.remove("is-active");
        // Same preload reset as makeClone: never let a clone fetch on attach.
        const cv = c.querySelector("video");
        if (cv) {
          cv.removeAttribute("autoplay");
          // Same src reset as makeClone: never let a clone fetch on attach.
          cv.removeAttribute("src");
          cv.setAttribute("preload", "none");
        }
        return c;
      };

      const CLONE_SETS = 2;
      for (let s = 0; s < CLONE_SETS; s++) {
        const before = document.createDocumentFragment();
        real.forEach((i) => before.appendChild(mkClone(i)));
        list.prepend(before);

        const after = document.createDocumentFragment();
        real.forEach((i) => after.appendChild(mkClone(i)));
        list.appendChild(after);
      }

      if (this._mobileThumb) this._updateThumbWidth(list, this._mobileThumb);

      // Scroll to the active item (selected variant's featured media), falling
      // back to the first real item. Synchronous on purpose: reading offsetLeft
      // forces the post-insert layout, so this jump lands in the SAME paint as
      // the clone insertion. Deferring it to rAF painted a frame at
      // scrollLeft 0 first — the prepend-clone zone, whose lazy images are
      // still blank — and the strip is already revealed via pdp-g--early, so
      // on load that frame showed as a flash + jump-back. Snap is disabled for
      // the assignment (same trick as _checkMobileInfiniteJump) so mandatory
      // snap can't animate a re-snap from the new position.
      const target =
        list.querySelector(
          ".pdp-g__mobile-item.is-active:not(.pdp-g__mobile-clone):not(.pdp-hidden)",
        ) ||
        list.querySelector(
          ".pdp-g__mobile-item:not(.pdp-g__mobile-clone):not(.pdp-hidden)",
        );
      if (target) {
        list.style.scrollSnapType = "none";
        list.scrollLeft = target.offsetLeft;
        requestAnimationFrame(() => {
          list.style.scrollSnapType = "";
        });
      }

      // Cloned slide videos start paused — hook playback up to the new set
      // (only once the page has finished loading).
      if (this._pageLoaded) this._observeMobileVideos();
    }

    /**
     * Physically reorder DOM so visible mobile items come first (sorted by
     * their current offsetLeft, which already reflects CSS flex `order`),
     * then hidden items. After this the CSS `order` property is cleared.
     * Mobile twin of _normalizeOrder().
     */
    _normalizeMobileOrder() {
      const list = this._mobileList;
      if (!list) return;
      const all = [
        ...list.querySelectorAll(
          ".pdp-g__mobile-item:not(.pdp-g__mobile-clone)",
        ),
      ];
      const visible = all.filter((t) => !t.classList.contains("pdp-hidden"));
      const hidden = all.filter((t) => t.classList.contains("pdp-hidden"));

      // Inline `order` is the primary key: offsetLeft alone reads 0 for every
      // item while the mobile viewport is display:none (desktop load), which
      // would clear the order styles without capturing their sequence.
      const ord = (t) => parseInt(t.style.order, 10) || 0;
      visible.sort((a, b) => ord(a) - ord(b) || a.offsetLeft - b.offsetLeft);

      [...visible, ...hidden].forEach((t) => {
        t.style.order = "";
        list.appendChild(t);
      });
    }

    /* Detect drift into clone zone and silently teleport back to real zone */
    _checkMobileInfiniteJump() {
      const list = this._mobileList;
      if (!list) return;

      const real = [
        ...list.querySelectorAll(
          ".pdp-g__mobile-item:not(.pdp-g__mobile-clone):not(.pdp-hidden)",
        ),
      ];
      if (!real.length) return;

      const firstReal = real[0];
      const lastReal = real[real.length - 1];

      // First append-clone (first clone that follows lastReal in DOM)
      let firstAppend = null;
      let pastLast = false;
      for (const child of list.children) {
        if (child === lastReal) {
          pastLast = true;
          continue;
        }
        if (
          pastLast &&
          child.classList.contains("pdp-g__mobile-clone") &&
          !child.classList.contains("pdp-hidden")
        ) {
          firstAppend = child;
          break;
        }
      }
      if (!firstAppend) return;

      const realZoneWidth = firstAppend.offsetLeft - firstReal.offsetLeft;
      if (realZoneWidth <= 0) return;
      const sl = list.scrollLeft;

      // Drift can now span multiple zones (two clone sets per side), so
      // normalise back into [firstReal, firstReal + zone) instead of
      // assuming we are at most one zone out.
      const rel = sl - firstReal.offsetLeft;
      const wrapped =
        ((rel % realZoneWidth) + realZoneWidth) % realZoneWidth;
      const delta = wrapped - rel;

      if (!delta) return;

      // Teleport instantly. Disable snap briefly so no re-snap animation
      // fires from the new position (clone and real items are identical).
      list.style.scrollSnapType = "none";
      list.scrollLeft = sl + delta;
      requestAnimationFrame(() => {
        list.style.scrollSnapType = "";
      });
    }

    /* Sync custom scrollbar thumb to position within the real zone */
    _syncMobileScrollbar() {
      const list = this._mobileList;
      const thumb = this._mobileThumb;
      if (!list || !thumb) return;

      const firstReal = list.querySelector(
        ".pdp-g__mobile-item:not(.pdp-g__mobile-clone):not(.pdp-hidden)",
      );
      const allReal = [
        ...list.querySelectorAll(
          ".pdp-g__mobile-item:not(.pdp-g__mobile-clone):not(.pdp-hidden)",
        ),
      ];
      const lastReal = allReal[allReal.length - 1];
      if (!firstReal || !lastReal) return;

      const realStart = firstReal.offsetLeft;
      const realZoneWidth =
        lastReal.offsetLeft + lastReal.offsetWidth - realStart;
      const maxScroll = realZoneWidth - list.clientWidth;
      if (maxScroll <= 0) return;

      // Normalize scrollLeft into the real zone (wraps on clone teleport)
      let rel = list.scrollLeft - realStart;
      rel = ((rel % realZoneWidth) + realZoneWidth) % realZoneWidth;
      const progress = Math.min(1, rel / maxScroll);

      const trackW = thumb.parentElement.clientWidth;
      const thumbW = thumb.offsetWidth;
      thumb.style.transform = `translateX(${progress * (trackW - thumbW)}px)`;
    }

    _updateThumbWidth(list, thumb) {
      const count =
        list.querySelectorAll(
          ".pdp-g__mobile-item:not(.pdp-g__mobile-clone):not(.pdp-hidden)",
        ).length || 1;
      const trackW = thumb.parentElement.clientWidth;
      const w = Math.max(20, Math.round(trackW / count));
      thumb.style.width = `${w}px`;
    }

    /* ═══════════════════════════════════════════════════════════
       Pinch-to-zoom sharpening (mobile)
       Native pinch zoom just magnifies the already-painted slide; it
       never re-fetches a sharper source. So we watch the visual
       viewport and, once the user zooms in, swap the slide they're
       viewing to a high-res render on demand. The default page load
       stays light — only the zoomed image upgrades, and only once.
    ═══════════════════════════════════════════════════════════ */
    _setupZoom() {
      const vv = window.visualViewport;
      if (!vv || !this._mobileList) return;

      this._onVVResize = () => {
        if (vv.scale <= ZOOM_MIN_SCALE) return;
        // Coalesce the burst of resize events a pinch fires into one pass.
        if (this._zoomRaf) return;
        this._zoomRaf = requestAnimationFrame(() => {
          this._zoomRaf = 0;
          this._upgradeZoomSlides();
        });
      };
      vv.addEventListener("resize", this._onVVResize, { passive: true });
    }

    /* Find the slide centred in the carousel and upgrade it plus its
       immediate neighbours (so a small pan after zooming stays crisp). */
    _upgradeZoomSlides() {
      const list = this._mobileList;
      if (!list) return;

      // Skip the work if the carousel isn't on screen — the user is
      // pinching something else on the page.
      const r = list.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (r.bottom <= 0 || r.top >= vh) return;

      const items = [
        ...list.querySelectorAll(".pdp-g__mobile-item:not(.pdp-hidden)"),
      ];
      if (!items.length) return;

      // offsetLeft shares scrollLeft's coordinate space (see _syncMobileScrollbar).
      const center = list.scrollLeft + list.clientWidth / 2;
      let bi = 0;
      let bd = Infinity;
      items.forEach((it, i) => {
        const d = Math.abs(it.offsetLeft + it.offsetWidth / 2 - center);
        if (d < bd) {
          bd = d;
          bi = i;
        }
      });

      [items[bi - 1], items[bi], items[bi + 1]].forEach((it) => {
        if (it) this._upgradeZoomImg(it.querySelector(":scope > img"));
      });
    }

    /* Swap one image to a high-res Shopify render. Idempotent per <img>. */
    _upgradeZoomImg(img) {
      if (!img || img.dataset.zoomHi) return;
      const base = img.currentSrc || img.src;
      if (!base) return;

      let url;
      try {
        url = new URL(base, window.location.href);
      } catch (_) {
        return;
      }

      // Liquid stamps the native resolution on the width attribute; cap the
      // request there (Shopify won't upscale) and at our ceiling.
      const orig = parseInt(img.getAttribute("width"), 10) || 0;
      const target = orig
        ? Math.min(orig, ZOOM_MAX_WIDTH)
        : ZOOM_MAX_WIDTH;
      const current = parseInt(url.searchParams.get("width"), 10) || 0;
      if (current >= target) {
        img.dataset.zoomHi = "1";
        return;
      }

      url.searchParams.set("width", String(target));
      const hi = url.toString();
      img.dataset.zoomHi = "1";
      // Override srcset too — otherwise the browser keeps choosing from the
      // original responsive candidates and the new src is ignored. A single
      // candidate with no descriptor is treated as 1x and always wins.
      img.srcset = hi;
      img.src = hi;
    }

    /* ═══════════════════════════════════════════════════════════
       PubSub: color/variant change
    ═══════════════════════════════════════════════════════════ */
    _onColorChange({ data }) {
      const productInfo = this.closest("product-info");
      if (productInfo && !productInfo.contains(data?.event?.target)) return;

      const target = data?.target;
      if (!target) return;

      const inputEl =
        target.tagName === "OPTION" ? target.closest("select") : target;
      const inputName = (inputEl?.name || "").toLowerCase();
      const colorOptionName = (
        this.dataset.colorOptionName || "color"
      ).toLowerCase();
      if (!inputName.startsWith(colorOptionName + "-")) return;

      const newColor = (target.value || "").toLowerCase().trim();
      if (!newColor) return;

      const isOos = this._isColorOos(newColor);
      this._filterByColor(newColor, isOos, true /* animate */);

      // Sync wishlist buttons to the currently selected variant.
      // Read directly from the product form input — always up to date by the
      // time optionValueSelectionChange fires (VariantSelects updates it first).
      const sectionId = this.dataset.section;
      const variantInput = document.querySelector(
        `#product-form-${sectionId} input[name="id"], product-form[data-section-id="${sectionId}"] input[name="id"]`,
      );
      if (variantInput?.value) this._syncWishlistBtns(variantInput.value);
    }

    _isColorOos(color) {
      try {
        const el = document.getElementById("ProductVariantsData");
        if (!el) return false;
        const variants = JSON.parse(el.textContent || "[]");
        const colorIdx = parseInt(this.dataset.colorOptionIdx || "0", 10);
        const matching = variants.filter(
          (v) =>
            (v["option" + (colorIdx + 1)] || "").toLowerCase().trim() === color,
        );
        return matching.length > 0 && matching.every((v) => !v.available);
      } catch (_) {
        return false;
      }
    }

    /**
     * The currently selected variant object (from #ProductVariantsData) —
     * the Shopify-canonical source for the "active" media.
     *
     * IMPORTANT: at optionValueSelectionChange time the product form's
     * input[name="id"] is still the OLD variant (it's only updated after the
     * async section fetch returns). So we resolve from the *current option
     * selection* in <variant-selects> instead: the just-clicked swatch/option
     * is already :checked / [selected] in the DOM. We match those values
     * against variant.options, then fall back to color-only, then (for
     * post-fetch callers) the form input. Returns null if nothing matches, so
     * callers fall back to the first image of the color.
     */
    _selectedVariant() {
      try {
        const el = document.getElementById("ProductVariantsData");
        if (!el) return null;
        const variants = JSON.parse(el.textContent || "[]");
        if (!Array.isArray(variants) || !variants.length) return null;

        const norm = (s) => (s || "").trim().toLowerCase();

        // Current option *values* (text), in option order — fresh at event time.
        const variantSelects =
          this.closest("product-info")?.querySelector("variant-selects") ||
          document.querySelector("variant-selects");
        const selected = variantSelects
          ? [
              ...variantSelects.querySelectorAll(
                "select option[selected], fieldset input:checked",
              ),
            ].map((opt) => norm(opt.value))
          : [];

        if (selected.length) {
          // Exact match on the full option set.
          const exact = variants.find(
            (v) =>
              (v.options || []).length === selected.length &&
              v.options.every((o, i) => norm(o) === selected[i]),
          );
          if (exact) return exact;

          // Fallback: first variant matching just the color option.
          const colorIdx = parseInt(this.dataset.colorOptionIdx || "0", 10);
          const color = selected[colorIdx];
          const byColor = variants.find(
            (v) => norm(v.options?.[colorIdx]) === color,
          );
          if (byColor) return byColor;
        }

        // Last resort: product form's current variant (correct after fetch).
        const sectionId = this.dataset.section;
        const variantInput = document.querySelector(
          `#product-form-${sectionId} input[name="id"], product-form[data-section-id="${sectionId}"] input[name="id"]`,
        );
        const variantId = variantInput?.value;
        return variantId
          ? variants.find((v) => String(v.id) === String(variantId)) || null
          : null;
      } catch (_) {
        return null;
      }
    }

    _filterByColor(color, isOos, animate = false) {
      const allThumbs = [
        ...this.querySelectorAll(".pdp-g__thumb:not(.pdp-g__clone)"),
      ];
      const allMain = [...this.querySelectorAll(".pdp-g__main-item")];

      const hasMatch =
        !isOos && allThumbs.some((t) => t.dataset.color === color);

      const shouldShow = (el) => {
        const mc = (el.dataset.color || "").toLowerCase();
        if (isOos) return mc === "out of stock" || mc === "carbon silk";
        if (hasMatch) return mc === color;
        return true;
      };

      // Shopify-canonical active media = the selected variant's featured media.
      // Honor it whenever it survives the color filter; otherwise fall back to
      // the first visible item (first image of the color). Mirrors the
      // active_is_visible logic in pdp-gallery.liquid so SSR and runtime agree.
      const sectionId = this.dataset.section;
      const selectedVariant = this._selectedVariant();
      const preferredId = selectedVariant?.featured_media?.id
        ? `${sectionId}-${selectedVariant.featured_media.id}`
        : null;
      const isActiveTarget = (el) =>
        !!preferredId && el.dataset.mediaId === preferredId && shouldShow(el);

      // Mark the active mobile item (featured-if-visible, else first visible) so
      // _buildMobileClones scrolls the carousel to it.
      const markActiveMobile = () => {
        const items = [
          ...this.querySelectorAll(
            ".pdp-g__mobile-item:not(.pdp-g__mobile-clone)",
          ),
        ];
        const active =
          items.find((el) => isActiveTarget(el)) ||
          items.find((el) => shouldShow(el));
        items.forEach((el) => el.classList.toggle("is-active", el === active));
      };

      // For OOS: physically reorder real thumbs (OOS first, carbon silk second)
      // so the infinite clones reflect the correct visual order
      if (isOos) {
        const oosItems = allThumbs.filter(
          (t) => t.dataset.color === "out of stock",
        );
        const csItems = allThumbs.filter(
          (t) => t.dataset.color === "carbon silk",
        );
        [...oosItems, ...csItems].forEach((t) =>
          this._thumbsList?.appendChild(t),
        );

        // Same for the mobile strip — without this the B&W OOS fallbacks stay
        // wherever they sit in the gallery (often last), so the carousel
        // opens on carbon silk and the fallbacks never lead.
        const mobileReal = [
          ...this.querySelectorAll(
            ".pdp-g__mobile-item:not(.pdp-g__mobile-clone)",
          ),
        ];
        const mobileOos = mobileReal.filter(
          (t) => t.dataset.color === "out of stock",
        );
        const mobileCs = mobileReal.filter(
          (t) => t.dataset.color === "carbon silk",
        );
        [...mobileOos, ...mobileCs].forEach((t) =>
          this._mobileList?.appendChild(t),
        );
      }

      allThumbs.forEach((el) => {
        el.classList.toggle("pdp-hidden", !shouldShow(el));
        el.style.order = ""; // clear any CSS order; DOM order is now canonical
      });

      // Activate the canonical media. On a user color change (animate=true) the
      // switch is decode-gated: the outgoing slide stays on screen until the
      // incoming image has actually decoded (capped), so the viewer crossfades
      // between two painted frames instead of flashing an empty box that pops
      // in when the network catches up.
      const activeMain =
        allMain.find((el) => isActiveTarget(el)) ||
        allMain.find((el) => shouldShow(el));
      const prevMain = allMain.find((el) =>
        el.classList.contains("is-active"),
      );
      // Every run supersedes any in-flight gated crossfade — a stale gate
      // resolving later must never re-activate an outdated slide.
      this._mainColorToken = (this._mainColorToken || 0) + 1;
      const token = this._mainColorToken;
      this._mainGate = null;
      if (activeMain) {
        // Keep exactly ONE eager main image. The active image was likely
        // server-rendered loading="lazy" (it wasn't the initial active media),
        // so revealing it would defer the fetch and pop in late — promote it to
        // eager/high. Demote every other main image back to lazy so repeated
        // color changes don't leave a trail of eager images all fetching at once.
        allMain.forEach((m) => {
          const i = m.querySelector("img");
          if (!i) return;
          const isActive = m === activeMain;
          i.loading = isActive ? "eager" : "lazy";
          i.setAttribute("fetchpriority", isActive ? "high" : "auto");
        });

        const img = activeMain.querySelector("img");
        const activate = () => {
          allMain.forEach((m) => {
            const isActive = m === activeMain;
            m.classList.toggle("is-active", isActive);
            const video = m.querySelector("video");
            if (video && !isActive) {
              video.pause();
              video.currentTime = 0;
            }
          });
        };

        if (animate && img && prevMain && prevMain !== activeMain) {
          // Handshake with setActiveMedia: the thumb-rebuild rAF below calls it
          // with this same media id — the gate makes it defer to us instead of
          // activating the not-yet-decoded slide early.
          this._mainGate = { mediaId: activeMain.dataset.mediaId, token };
          this._decodeWithTimeout(img, 450).then(() => {
            if (token !== this._mainColorToken) return; // superseded
            this._mainGate = null;
            activate();
            // Retire the outgoing slide once the 0.35s crossfade has finished.
            setTimeout(() => {
              if (token !== this._mainColorToken) return;
              prevMain.classList.toggle("pdp-hidden", !shouldShow(prevMain));
            }, 400);
          });
        } else {
          activate();
          if (img) this._decodeWithTimeout(img, 250); // avoid a flash of empty frame
        }
      }

      // Desktop main items: toggle visibility (crossfade handled by CSS .is-active).
      // During a decode-gated change the outgoing slide is skipped here and
      // retired by the gate above, after the crossfade. (_mainGate is only
      // truthy here when THIS run created it — every run clears it on entry.)
      const gatedPrev = this._mainGate ? prevMain : null;
      allMain.forEach((el) => {
        if (el === gatedPrev) return;
        el.classList.toggle("pdp-hidden", !shouldShow(el));
      });

      // Rebuild desktop thumb clones with updated visibility/order
      requestAnimationFrame(() => {
        // A newer color run may have already filtered the DOM before this rAF
        // flushes (rapid clicks / rAF starvation). Bail so a stale run can't
        // call setActiveMedia with the wrong first-visible thumb, which would
        // cancel the latest run's decode gate and reintroduce the blank/pop-in.
        if (token !== this._mainColorToken) return;
        this._buildInfiniteClones();

        const visThumbs = [
          ...(this._thumbsList?.querySelectorAll(
            ".pdp-g__thumb:not(.pdp-g__clone):not(.pdp-hidden)",
          ) || []),
        ];
        const activeThumb =
          visThumbs.find((t) => isActiveTarget(t)) || visThumbs[0];
        if (activeThumb) {
          this.setActiveMedia(activeThumb.dataset.mediaId, true);
          requestAnimationFrame(() => this._jumpToRealZone());
        }
      });

      // Mobile gallery
      if (animate) {
        // User colour tap → smooth fade-out → swap (hidden) → decode → fade-in
        this._transitionMobileColor(shouldShow, markActiveMobile);
      } else {
        // First-paint / URL correction → instant (FOUC guard handles the reveal)
        this.querySelectorAll(".pdp-g__mobile-item").forEach((el) => {
          el.classList.toggle("pdp-hidden", !shouldShow(el));
        });
        markActiveMobile();
        if (this._mobileThumb)
          this._mobileThumb.style.transform = "translateX(0)";
        requestAnimationFrame(() => this._buildMobileClones());
      }
    }

    /* ═══════════════════════════════════════════════════════════
       Mobile colour transition
       Fade the list out, swap visibility + rebuild clones while
       hidden, decode the incoming first image, then fade back in.
       Replaces the hard display:none cut + lazy-image pop-in with a
       smooth crossfade-style change that matches the rest of the site.
    ═══════════════════════════════════════════════════════════ */
    _transitionMobileColor(shouldShow, markActiveMobile) {
      const list = this._mobileList;

      const swap = () => {
        this.querySelectorAll(".pdp-g__mobile-item").forEach((el) => {
          el.classList.toggle("pdp-hidden", !shouldShow(el));
        });
        if (markActiveMobile) markActiveMobile();
        if (this._mobileThumb)
          this._mobileThumb.style.transform = "translateX(0)";
        this._buildMobileClones();
      };

      if (!list) {
        swap();
        return;
      }

      const FADE = 150;

      // Guard against rapid taps: only the latest transition reveals/swaps.
      this._mobileColorToken = (this._mobileColorToken || 0) + 1;
      const token = this._mobileColorToken;

      // Fetch + decode the incoming first image during the fade-out so it's
      // painted before reveal (kills the blank-box-then-pop-in flicker).
      const incoming = this._firstMatchingMobileImg(shouldShow);
      let decoded;
      if (incoming) {
        incoming.loading = "eager";
        incoming.setAttribute("fetchpriority", "high");
        // 450ms cap: 250ms rarely covered a cold-cache fetch, so the reveal
        // used to land with a still-loading image that popped in late.
        decoded = this._decodeWithTimeout(incoming, 450);
      } else {
        decoded = Promise.resolve();
      }

      // Fade out (own inline transition so first-paint reveal timing is untouched)
      list.style.transition = `opacity ${FADE}ms ease`;
      list.style.opacity = "0";

      const fadedOut = new Promise((res) => setTimeout(res, FADE));
      Promise.all([fadedOut, decoded]).then(() => {
        if (token !== this._mobileColorToken) return; // superseded by a newer tap
        swap();
        // Reveal after the rebuilt clones + scroll reset have settled.
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            if (token !== this._mobileColorToken) return;
            list.style.opacity = "1";
          }),
        );
      });
    }

    /** First mobile item that will be visible for the new colour → its <img> */
    _firstMatchingMobileImg(shouldShow) {
      const items = [
        ...this.querySelectorAll(
          ".pdp-g__mobile-item:not(.pdp-g__mobile-clone)",
        ),
      ];
      const match = items.find((el) => shouldShow(el));
      return match ? match.querySelector("img") : null;
    }

    /** Resolve when the image is decoded, or after `ms` — never hangs. */
    _decodeWithTimeout(img, ms) {
      if (!img) return Promise.resolve();
      let decodePromise;
      try {
        decodePromise = img.decode();
      } catch (e) {
        decodePromise = Promise.resolve();
      }
      return Promise.race([
        Promise.resolve(decodePromise).catch(() => {}),
        new Promise((res) => setTimeout(res, ms)),
      ]);
    }
  }

  customElements.define("pdp-gallery", PdpGallery);
}

